import { AlertDialog, BodyText, Button, Callout, toast } from "@/components/ui";
import { TRACKERS } from "@/constants/Trackers";
import { useSubmitOrder } from "@/features/Charting/components/Labs/network/useSubmitOrder";
import { trackEvent } from "@/lib/tracking/tracking";
import { useState } from "react";
import { useVoidFailedOrderByTest } from "../network/useVoidFailedOrderByTest";

export const FailedOrder = ({
  patientId,
  serviceRequestId,
  requestGroupId,
  encounterId,
  canRetry,
  needsRefundIfCancelled,
}: {
  patientId: string;
  serviceRequestId: string;
  requestGroupId: string;
  encounterId: string;
  canRetry: boolean;
  needsRefundIfCancelled: boolean;
}) => {
  const [showRefundPrompt, setShowRefundPrompt] = useState(false);
  const {
    mutate: voidFailedOrderByTest,
    isPending: isVoidingFailedOrderByTest,
  } = useVoidFailedOrderByTest({
    patientId,
    serviceRequestId,
    encounterId,
  });

  const handleCancelOrder = () => {
    if (needsRefundIfCancelled) {
      setShowRefundPrompt(true);
    } else {
      voidFailedOrderByTest({ skipPaymentCheck: false });
    }
  };

  const { mutateAsync: submitOrder, isPending: isSubmittingOrder } =
    useSubmitOrder({
      patientId,
      encounterId,
      requestGroupId,
      markOnHold: false,
      serviceRequestId,
    });

  const handleResubmitOrder = async () => {
    trackEvent(TRACKERS.RESUBMIT_LAB_ORDER);
    try {
      await submitOrder();
    } catch {
      toast.error("An error occurred resubmitting the order.");
    }
  };

  return (
    <>
      <Callout
        variant="danger"
        title="Submission Failed"
        className="flex flex-col gap-2"
      >
        <BodyText size="sm">
          This order failed to send to the lab.{" "}
          {canRetry
            ? "If resending the lab fails, the order will need to be cancelled and re-ordered."
            : "The order will need to be cancelled and re-ordered."}
        </BodyText>
        <div className="flex gap-2">
          <Button
            size="xs"
            className="w-fit"
            variant="danger"
            onClick={handleCancelOrder}
            disabled={isSubmittingOrder}
            isLoading={isVoidingFailedOrderByTest}
          >
            Cancel Order
          </Button>
          {canRetry && (
            <Button
              size="xs"
              className="w-fit"
              variant="primary"
              onClick={handleResubmitOrder}
              disabled={isVoidingFailedOrderByTest}
              isLoading={isSubmittingOrder}
            >
              Resubmit Order
            </Button>
          )}
        </div>
      </Callout>
      <AlertDialog
        isOpen={showRefundPrompt}
        onOpenChange={setShowRefundPrompt}
        isControlled
        title="Warning: Pre-Paid Order"
        body="This order has been paid for by the patient and will not be automatically refunded. Before canceling, contact support at support@curai.com to manually initiate a refund process for this patient."
        actionLabel="Cancel Order"
        cancelLabel="Go Back"
        onAction={() => {
          voidFailedOrderByTest({ skipPaymentCheck: true });
          setShowRefundPrompt(false);
        }}
        onCancel={() => {
          setShowRefundPrompt(false);
        }}
        // TODO: Remove this once we fix the AlertDialogProvider
        // biome-ignore lint/complexity/noUselessFragments: We need to pass a trigger element to the AlertDialog so the AlertDialogProvider doesn't render the default trigger element
        triggerElement={<></>}
      />
    </>
  );
};
